import { __assign } from 'tslib';
import React, { forwardRef, useState, useRef, useMemo, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import { usePopper } from 'react-popper';
import { CSSTransition } from 'react-transition-group';
import { ResizeObserver } from '@juggle/resize-observer';
import cn from 'classnames';
import maxSize from 'popper-max-size-modifier';
import { Portal } from '@alfalab/core-components-portal/esm';
import { Stack } from '@alfalab/core-components-stack/esm';
import { useLayoutEffect_SAFE_FOR_SSR } from '@alfalab/hooks';
import { stackingOrder } from '@alfalab/stack-context';

var styles = {"component":"popover__component_chanz","inner":"popover__inner_chanz","scrollableContent":"popover__scrollableContent_chanz","arrow":"popover__arrow_chanz","enter":"popover__enter_chanz","enterActive":"popover__enterActive_chanz","exit":"popover__exit_chanz","exitActive":"popover__exitActive_chanz"};
require('./index.css')

var DEFAULT_TRANSITION = {
    timeout: 150,
};
var CSS_TRANSITION_CLASS_NAMES = {
    enter: styles.enter,
    enterActive: styles.enterActive,
    exit: styles.exit,
    exitActive: styles.exitActive,
};
var availableHieghtModifier = {
    name: 'availableHeight',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['maxSize'],
    fn: function (_a) {
        var _b = _a.state, modifiersData = _b.modifiersData, popper = _b.elements.popper;
        var height = modifiersData.maxSize.height;
        var content = popper.querySelector(".".concat(styles.scrollableContent));
        if (content && !content.style.maxHeight) {
            content.style.maxHeight = "".concat(height, "px");
        }
    },
};
var DEFAULT_OFFSET = [0, 0];
// Минимальное расстояние стрелки до края поповера
var MIN_DISTANCE_TO_EDGE = 24;
function getArrowPadding(_a) {
    var placement = _a.placement;
    if (placement === 'right-end' || placement === 'left-end') {
        return { top: MIN_DISTANCE_TO_EDGE, right: 0, bottom: 0, left: 0 };
    }
    if (placement === 'top-start' || placement === 'bottom-start') {
        return { top: 0, right: MIN_DISTANCE_TO_EDGE, bottom: 0, left: 0 };
    }
    if (placement === 'right-start' || placement === 'left-start') {
        return { top: 0, right: 0, bottom: MIN_DISTANCE_TO_EDGE, left: 0 };
    }
    if (placement === 'top-end' || placement === 'bottom-end') {
        return { top: 0, right: 0, bottom: 0, left: MIN_DISTANCE_TO_EDGE };
    }
    return 0;
}
var Popover = forwardRef(function (_a, ref) {
    var children = _a.children, getPortalContainer = _a.getPortalContainer, _b = _a.transition, transition = _b === void 0 ? DEFAULT_TRANSITION : _b, anchorElement = _a.anchorElement, useAnchorWidth = _a.useAnchorWidth, _c = _a.offset, offset = _c === void 0 ? DEFAULT_OFFSET : _c, _d = _a.withArrow, withArrow = _d === void 0 ? false : _d, _e = _a.withTransition, withTransition = _e === void 0 ? true : _e, _f = _a.position, position = _f === void 0 ? 'left' : _f, preventFlip = _a.preventFlip, popperClassName = _a.popperClassName, arrowClassName = _a.arrowClassName, className = _a.className, open = _a.open, dataTestId = _a.dataTestId, update = _a.update, _g = _a.transitionDuration, transitionDuration = _g === void 0 ? "".concat(transition.timeout, "ms") : _g, _h = _a.zIndex, zIndex = _h === void 0 ? stackingOrder.POPOVER : _h, fallbackPlacements = _a.fallbackPlacements, _j = _a.preventOverflow, preventOverflow = _j === void 0 ? true : _j, _k = _a.availableHeight, availableHeight = _k === void 0 ? false : _k;
    var _l = useState(anchorElement), referenceElement = _l[0], setReferenceElement = _l[1];
    var _m = useState(null), popperElement = _m[0], setPopperElement = _m[1];
    var _o = useState(null), arrowElement = _o[0], setArrowElement = _o[1];
    var updatePopperRef = useRef();
    var popperRef = useRef(null);
    var innerRef = useRef(null);
    var popperModifiers = useMemo(function () {
        var modifiers = [{ name: 'offset', options: { offset: offset } }];
        if (withArrow) {
            modifiers.push({
                name: 'arrow',
                options: {
                    element: arrowElement,
                    padding: getArrowPadding,
                },
            });
        }
        if (preventFlip) {
            modifiers.push({ name: 'flip', options: { fallbackPlacements: [] } });
        }
        if (fallbackPlacements) {
            modifiers.push({ name: 'flip', options: { fallbackPlacements: fallbackPlacements } });
        }
        if (preventOverflow) {
            modifiers.push({ name: 'preventOverflow', options: { mainAxis: false } });
        }
        if (availableHeight) {
            modifiers.push(__assign(__assign({}, maxSize), { options: {} }));
            modifiers.push(__assign(__assign({}, availableHieghtModifier), { options: {} }));
        }
        return modifiers;
    }, [
        offset,
        withArrow,
        preventFlip,
        fallbackPlacements,
        preventOverflow,
        availableHeight,
        arrowElement,
    ]);
    var _p = usePopper(referenceElement, popperElement, {
        placement: position,
        modifiers: popperModifiers,
    }), popperStyles = _p.styles, attributes = _p.attributes, updatePopper = _p.update;
    if (updatePopper) {
        updatePopperRef.current = updatePopper;
    }
    useLayoutEffect_SAFE_FOR_SSR(function () {
        setReferenceElement(anchorElement);
    }, [anchorElement]);
    useEffect(function () {
        if (updatePopper) {
            updatePopper();
        }
    }, [updatePopper, arrowElement, children]);
    useEffect(function () {
        if (update && updatePopper) {
            // eslint-disable-next-line no-param-reassign
            update.current = updatePopper;
        }
    });
    useEffect(function () {
        if (useAnchorWidth) {
            var updatePopoverWidth = function () { var _a; return (_a = updatePopperRef.current) === null || _a === void 0 ? void 0 : _a.call(updatePopperRef); };
            var ResizeObserver_1 = window.ResizeObserver || ResizeObserver;
            var observer_1 = new ResizeObserver_1(updatePopoverWidth);
            if (anchorElement) {
                observer_1.observe(anchorElement);
            }
            return function () {
                observer_1.disconnect();
            };
        }
        return function () { return ({}); };
    }, [anchorElement, useAnchorWidth]);
    var renderContent = function (computedZIndex) {
        var _a;
        var _b;
        return (React.createElement("div", __assign({ ref: mergeRefs([ref, popperRef, setPopperElement]), style: __assign(__assign({ zIndex: computedZIndex, width: useAnchorWidth ? referenceElement === null || referenceElement === void 0 ? void 0 : referenceElement.offsetWidth : undefined }, popperStyles.popper), (((_b = popperStyles.popper) === null || _b === void 0 ? void 0 : _b.transform) ? null : { visibility: 'hidden' })), "data-test-id": dataTestId, className: cn(styles.component, className) }, attributes.popper),
            React.createElement("div", { className: cn(styles.inner, popperClassName), ref: innerRef },
                React.createElement("div", { className: cn((_a = {}, _a[styles.scrollableContent] = availableHeight, _a)) }, children),
                withArrow && (React.createElement("div", { ref: setArrowElement, style: popperStyles.arrow, className: cn(styles.arrow, arrowClassName) })))));
    };
    return (React.createElement(Stack, { value: zIndex }, function (computedZIndex) { return (React.createElement(Portal, { getPortalContainer: getPortalContainer }, withTransition ? (React.createElement(CSSTransition, __assign({ unmountOnExit: true, classNames: CSS_TRANSITION_CLASS_NAMES, nodeRef: popperRef }, transition, { in: open, onEntering: function (node, isAppearing) {
            var _a;
            // Меняем transition-duration только в случае, если передано значение отличное от дефолтного.
            if (innerRef.current &&
                transitionDuration !== "".concat(DEFAULT_TRANSITION.timeout, "ms")) {
                innerRef.current.style.setProperty('transition-duration', transitionDuration);
            }
            (_a = transition === null || transition === void 0 ? void 0 : transition.onEntering) === null || _a === void 0 ? void 0 : _a.call(transition, node, isAppearing);
        } }), renderContent(computedZIndex))) : (open && renderContent(computedZIndex)))); }));
});
Popover.displayName = 'Popover';

export { Popover };
